import type { ReactElement, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import {
  CARD_ACCENT_CLASSES,
  type AlertCardCategory,
} from './alert-detail-cards.helpers';

interface AccentCardProps {
  category: AlertCardCategory;
  /** Uppercase card title, e.g. "WHAT THIS MEANS". */
  title: string;
  children: ReactNode;
  className?: string;
}

/**
 * KIR-4481 — a bordered card with a coloured left accent and a category-coloured
 * title (small square bullet + uppercase label), the building block of the
 * redesigned Alert Details panel.
 */
export function AccentCard({
  category,
  title,
  children,
  className,
}: AccentCardProps): ReactElement {
  const accent = CARD_ACCENT_CLASSES[category];
  return (
    <div
      data-testid={`alert-card-${category}`}
      className={cn(
        'min-w-0 rounded-lg border border-l-[3px] bg-white p-3 dark:bg-slate-800',
        accent.border,
        className
      )}
    >
      <div className="mb-2 flex items-center gap-1.5">
        <span
          aria-hidden
          className={cn('h-2 w-2 rounded-[2px]', accent.bullet)}
        />
        <span
          className={cn(
            'text-xs font-semibold uppercase tracking-wide',
            accent.title
          )}
        >
          {title}
        </span>
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

AccentCard.displayName = 'AccentCard';
